import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { TagsInput } from "@/components/common/TagsInput";
import { norm, useTaxonomy } from "@/lib/red";

export const ServiceSelect = ({ tax, value, onChange, testid = "clasif-principal-select" }) => (
  <select data-testid={testid} className="c-input" value={value || ""} onChange={(e) => onChange(e.target.value)}>
    <option value="">Elige el servicio…</option>
    {tax.areas.map((a) => (
      <optgroup key={a.clave} label={a.nombre}>
        {tax.servicios.filter((s) => s.area === a.clave).map((s) => <option key={s.clave} value={s.clave}>{s.nombre}</option>)}
      </optgroup>
    ))}
  </select>
);

const ExtraServices = ({ tax, value, principal, onChange }) => {
  const [q, setQ] = useState("");
  const list = useMemo(() => tax.servicios.filter((s) => s.clave !== principal && (!q || norm(`${s.nombre} ${s.sinonimos.join(" ")}`).includes(norm(q)))), [tax, q, principal]);
  return (
    <div className="space-y-1.5">
      <input data-testid="clasif-extra-search" className="c-input !py-2 text-sm" placeholder="Buscar otros servicios…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div data-testid="clasif-extra-list" className="c-input !p-2 max-h-40 overflow-y-auto space-y-1">
        {list.map((s) => (
          <label key={s.clave} className="flex items-center gap-2 text-sm px-1">
            <input type="checkbox" checked={value.includes(s.clave)} onChange={(e) => onChange(e.target.checked ? [...value, s.clave] : value.filter((x) => x !== s.clave))} />
            {s.nombre}
          </label>
        ))}
      </div>
    </div>
  );
};

export const ClassificationSheet = ({ open, onOpenChange, contact, onSave }) => {
  const tax = useTaxonomy();
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      const principal = contact.servicio_principal || contact.servicio_sugerido || "";
      const extra = contact.servicio_principal ? contact.servicios : contact.servicios_sugeridos;
      setF({ servicio_principal: principal, servicios: (extra || []).filter((s) => s !== principal),
        ciudad_normalizada: contact.ciudad_normalizada || contact.city || "", etiquetas_ia: contact.etiquetas_ia || [] });
    }
  }, [open, contact]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSave({ ...f, servicios: [f.servicio_principal, ...f.servicios] }); } finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" data-testid="clasif-sheet" className="max-h-[92vh] overflow-y-auto rounded-t-2xl pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading">Clasificación en la red</SheetTitle>
          <SheetDescription>Tu red solo verá el servicio y la ciudad, nunca el nombre ni el teléfono.</SheetDescription>
        </SheetHeader>
        {!tax ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin c-muted" /></div> : (
          <form onSubmit={submit} className="mt-4 space-y-3 max-w-md mx-auto">
            <div className="space-y-1"><p className="text-xs font-medium c-text-2">Servicio principal *</p><ServiceSelect tax={tax} value={f.servicio_principal} onChange={(v) => setF((s) => ({ ...s, servicio_principal: v }))} /></div>
            <div className="space-y-1"><p className="text-xs font-medium c-text-2">Otros servicios</p><ExtraServices tax={tax} value={f.servicios || []} principal={f.servicio_principal} onChange={(v) => setF((s) => ({ ...s, servicios: v }))} /></div>
            <div className="space-y-1"><p className="text-xs font-medium c-text-2">Ciudad</p><input data-testid="clasif-ciudad-input" className="c-input" value={f.ciudad_normalizada || ""} onChange={(e) => setF((s) => ({ ...s, ciudad_normalizada: e.target.value }))} /></div>
            <div className="space-y-1"><p className="text-xs font-medium c-text-2">Etiquetas</p><TagsInput testid="clasif-tags-input" value={f.etiquetas_ia || []} onChange={(v) => setF((s) => ({ ...s, etiquetas_ia: v }))} /></div>
            <button data-testid="clasif-save-btn" disabled={busy || !f.servicio_principal} className="c-btn-primary w-full py-3.5 mt-2 flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar clasificación
            </button>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
};
